import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CottageIcon from '@mui/icons-material/Cottage';
import HomeIcon from '@mui/icons-material/Home';
import VillaIcon from '@mui/icons-material/Villa';
import { HOUSE_TYPES } from '../../utils/costRates';

const StepHouseInfo = ({
  data,
  onChange,
  onNext,
  onBack,
  language = 'en'
}) => {
  const content = {
    en: {
      stepLabel: 'Step 1 of 3',
      title: 'House Information',
      houseTypeLabel: 'House Type',
      areaLabel: 'Built-Up Area',
      areaHelper: 'Typical range: 800 - 3,000 sq ft',
      floorsLabel: 'Number of Floors',
      floor: 'Floor',
      floors: 'Floors',
      continueBtn: 'Continue',
      backBtn: 'Back',
      required: 'Please fill in all fields'
    },
    my: {
      stepLabel: 'Langkah 1 daripada 3',
      title: 'Maklumat Rumah',
      houseTypeLabel: 'Jenis Rumah',
      areaLabel: 'Luas Binaan',
      areaHelper: 'Julat tipikal: 800 - 3,000 kaki persegi',
      floorsLabel: 'Bilangan Tingkat',
      floor: 'Tingkat',
      floors: 'Tingkat',
      continueBtn: 'Teruskan',
      backBtn: 'Kembali',
      required: 'Sila isi semua ruangan'
    }
  };

  const t = content[language] || content.en;

  const houseTypeIcons = {
    kampung: <CottageIcon sx={{ fontSize: 28 }} />,
    terrace: <HomeIcon sx={{ fontSize: 28 }} />,
    bungalow: <VillaIcon sx={{ fontSize: 28 }} />
  };

  const handleHouseTypeChange = (event, newType) => {
    if (newType !== null) {
      onChange({ ...data, houseType: newType });
    }
  };

  const handleAreaChange = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    onChange({ ...data, builtUpArea: value ? parseInt(value, 10) : '' });
  };

  const handleFloorsChange = (event, newFloors) => {
    if (newFloors !== null) {
      onChange({ ...data, floors: newFloors });
    }
  };

  const isValid = data.houseType && data.builtUpArea >= 400 && data.floors;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      {/* Progress indicator */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.stepLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground' }} />
        </Box>
      </Box>

      {/* Title */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 4 }}>
        {t.title}
      </Typography>

      {/* House Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          {t.houseTypeLabel}
        </Typography>
        <ToggleButtonGroup
          value={data.houseType}
          exclusive
          onChange={handleHouseTypeChange}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '& .MuiToggleButton-root': {
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              p: 2,
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none',
              '&.Mui-selected': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.light'
                }
              }
            }
          }}
        >
          {Object.entries(HOUSE_TYPES).map(([key, config]) => (
            <ToggleButton key={key} value={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: data.houseType === key ? 'primary.main' : 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    color: data.houseType === key ? 'white' : 'text.secondary',
                    transition: 'all 0.2s'
                  }}
                >
                  {houseTypeIcons[key]}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {language === 'my' ? config.labelMy : config.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'my' ? config.descriptionMy : config.description}
                  </Typography>
                </Box>
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Built-Up Area */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          {t.areaLabel}
        </Typography>
        <TextField
          fullWidth
          type="text"
          inputMode="numeric"
          value={data.builtUpArea || ''}
          onChange={handleAreaChange}
          placeholder="1200"
          InputProps={{
            endAdornment: <InputAdornment position="end">sq ft</InputAdornment>,
            sx: { fontSize: '1.25rem' }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
        <FormHelperText sx={{ mt: 1 }}>{t.areaHelper}</FormHelperText>
      </Box>

      {/* Number of Floors */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
          {t.floorsLabel}
        </Typography>
        <ToggleButtonGroup
          value={data.floors}
          exclusive
          onChange={handleFloorsChange}
          sx={{
            display: 'flex',
            gap: 1.5,
            '& .MuiToggleButton-root': {
              flex: 1,
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              py: 2,
              '&.Mui-selected': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.light'
                }
              }
            }
          }}
        >
          <ToggleButton value={1}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>1</Typography>
              <Typography variant="body2" color="text.secondary">{t.floor}</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value={2}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>2</Typography>
              <Typography variant="body2" color="text.secondary">{t.floors}</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>3</Typography>
              <Typography variant="body2" color="text.secondary">{t.floors}</Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t.backBtn}
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!isValid}
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {t.continueBtn}
        </Button>
      </Box>
    </Box>
  );
};

export default StepHouseInfo;
